import { TestResults } from '../../../../../types';

export interface ImplementationPhase {
  title: string;
  description: string;
  duration: string;
  actions: string[];
  milestones: string[];
}

export const getImplementationPhases = (results: TestResults): ImplementationPhase[] => {
  const weakestCategories = [...results.categories]
    .sort((a, b) => a.percentage - b.percentage)
    .slice(0, 3);

  const phases: ImplementationPhase[] = [
    {
      title: 'Phase 1: Foundation Building (Months 1-3)',
      description: 'Focus on critical gaps and build fundamental export capabilities',
      duration: '3 months',
      actions: [
        'Address the lowest-scoring category first',
        'Establish basic export documentation processes',
        'Secure initial funding and financial planning',
        'Begin market research for target countries',
        'Set up basic compliance and legal framework'
      ],
      milestones: [
        'Complete export business plan',
        'Secure initial funding commitments',
        'Establish documentation workflow',
        'Identify target markets'
      ]
    },
    {
      title: 'Phase 2: Capability Development (Months 4-6)',
      description: 'Strengthen capabilities and prepare for market entry',
      duration: '3 months',
      actions: [
        'Complete product adaptation and certification',
        'Establish logistics and distribution partnerships',
        'Develop digital presence and marketing materials',
        'Implement compliance and risk management systems',
        'Build relationships with key service providers'
      ],
      milestones: [
        'Obtain necessary product certifications',
        'Establish logistics partnerships',
        'Launch international website',
        'Complete compliance framework'
      ]
    },
    {
      title: 'Phase 3: Market Entry & Launch (Months 7-12)',
      description: 'Execute export strategy and monitor performance',
      duration: '6 months',
      actions: [
        'Launch pilot export operations',
        'Execute marketing and sales strategies',
        'Monitor and optimize operations',
        'Build customer relationships',
        'Scale successful initiatives'
      ],
      milestones: [
        'Complete first export shipment',
        'Establish customer base',
        'Achieve target revenue goals',
        'Optimize operational processes'
      ]
    }
  ];

  return phases;
};

export const getImplementationTimeline = (categoryId: string): {
  immediate: string[];
  shortTerm: string[];
  longTerm: string[];
} => {
  const timelines: Record<string, {
    immediate: string[];
    shortTerm: string[];
    longTerm: string[];
  }> = {
    'business-plan': {
      immediate: ['Assess current export readiness', 'Define export objectives'],
      shortTerm: ['Develop comprehensive business plan', 'Allocate resources'],
      longTerm: ['Implement and monitor plan', 'Regular reviews and updates']
    },
    'finance': {
      immediate: ['Assess funding needs', 'Contact trade finance banks'],
      shortTerm: ['Secure funding facilities', 'Implement risk management'],
      longTerm: ['Monitor cash flow', 'Optimize financial strategies']
    },
    'documentation': {
      immediate: ['Create documentation checklist', 'Train key staff'],
      shortTerm: ['Implement document management system', 'Establish workflows'],
      longTerm: ['Continuous process improvement', 'Regular compliance audits']
    },
    'product': {
      immediate: ['Assess product compliance needs', 'Research target market requirements'],
      shortTerm: ['Obtain certifications', 'Adapt packaging and labeling'],
      longTerm: ['Monitor product performance', 'Continuous improvement']
    },
    'compliance': {
      immediate: ['Review current compliance status', 'Identify gaps'],
      shortTerm: ['Implement compliance procedures', 'Train staff'],
      longTerm: ['Regular compliance monitoring', 'Stay updated on regulations']
    },
    'market': {
      immediate: ['Identify target markets', 'Begin market research'],
      shortTerm: ['Conduct detailed market analysis', 'Identify partners'],
      longTerm: ['Build market presence', 'Expand to new markets']
    },
    'digital': {
      immediate: ['Audit current digital presence', 'Plan improvements'],
      shortTerm: ['Implement website and e-commerce', 'Launch digital marketing'],
      longTerm: ['Optimize digital strategies', 'Expand digital capabilities']
    },
    'logistics': {
      immediate: ['Research logistics providers', 'Understand shipping requirements'],
      shortTerm: ['Establish logistics partnerships', 'Implement tracking systems'],
      longTerm: ['Optimize logistics costs', 'Expand shipping options']
    },
    'legal': {
      immediate: ['Consult with trade lawyers', 'Review legal requirements'],
      shortTerm: ['Implement legal framework', 'Protect intellectual property'],
      longTerm: ['Monitor legal compliance', 'Update agreements as needed']
    },
    'risk': {
      immediate: ['Conduct risk assessment', 'Identify major risks'],
      shortTerm: ['Implement risk management procedures', 'Obtain insurance'],
      longTerm: ['Monitor and update risk strategies', 'Regular risk reviews']
    }
  };

  return timelines[categoryId] || {
    immediate: ['Assess current status', 'Identify immediate needs'],
    shortTerm: ['Develop improvement plan', 'Implement initial changes'],
    longTerm: ['Monitor progress', 'Continuous improvement']
  };
};

export const getResourcePriorities = (categoryId: string): {
  essential: string[];
  recommended: string[];
  optional: string[];
} => {
  const priorities: Record<string, {
    essential: string[];
    recommended: string[];
    optional: string[];
  }> = {
    'business-plan': {
      essential: ['Export business plan template', 'Market research tools'],
      recommended: ['Industry reports', 'Consulting services'],
      optional: ['Advanced planning software', 'Specialized training']
    },
    'finance': {
      essential: ['Trade finance consultation', 'Export costing tools'],
      recommended: ['Currency hedging services', 'Export credit insurance'],
      optional: ['Advanced financial software', 'Investment advisory']
    },
    'documentation': {
      essential: ['Documentation templates', 'Customs broker services'],
      recommended: ['Document management software', 'Training programs'],
      optional: ['Advanced automation tools', 'Specialized consulting']
    }
    // Add more categories as needed
  };

  return priorities[categoryId] || {
    essential: ['Basic guidance materials', 'Professional consultation'],
    recommended: ['Training programs', 'Industry resources'],
    optional: ['Advanced tools', 'Specialized services']
  };
};

export const calculateImplementationCost = (results: TestResults): {
  low: number;
  medium: number;
  high: number;
} => {
  const weakCategories = results.categories.filter(c => c.percentage < 60).length;
  const almostReadyCategories = results.categories.filter(c => c.percentage >= 60 && c.percentage < 80).length;

  // Base costs in USD (these are estimates)
  const baseCosts = {
    low: 5000 + (weakCategories * 2000) + (almostReadyCategories * 1000),
    medium: 15000 + (weakCategories * 5000) + (almostReadyCategories * 2500),
    high: 35000 + (weakCategories * 10000) + (almostReadyCategories * 5000)
  };

  return baseCosts;
};